/**
 * Check whether the user's browser supports a certain selector
 */
export function supportsSelector(selector: string) {
  const style = document.createElement('style')
  document.head.appendChild(style)
  const sheet = style.sheet
  if (!sheet) return false

  try {
    sheet.insertRule(`${selector} {}`)
    return true
  } catch {
    return false
  } finally {
    style.remove()
  }
}

/**
 * Create a random string of the requested length from a given alphabet
 */
export function randomString(
  length: number,
  alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
) {
  let text = ''

  for (let i = 0; i < length; i++) {
    text += alphabet.charAt(Math.floor(Math.random() * alphabet.length))
  }

  return text
}

/**
 * Create a comparator function that prioritizes the given sample values
 */
export function sortBySamples<T, U>(
  samples: T[],
  {
    getter,
    fallbackComparator,
  }: {
    getter?: (item: U) => T
    fallbackComparator?: (a: U, b: U) => number
  } = {},
) {
  return (a: U, b: U) => {
    const reducedA = getter?.(a) ?? (a as unknown as T)
    const reducedB = getter?.(b) ?? (b as unknown as T)
    const aIsSampled = samples.includes(reducedA)
    const bIsSampled = samples.includes(reducedB)

    if (aIsSampled && bIsSampled) {
      return samples.indexOf(reducedA) - samples.indexOf(reducedB)
    } else if (aIsSampled) {
      return -1
    } else if (bIsSampled) {
      return 1
    } else if (fallbackComparator) {
      return fallbackComparator(a, b)
    } else {
      return 0
    }
  }
}

/**
 * Throw an error if the given condition is not fulfilled
 */
export function assert(condition: unknown, message?: string): asserts condition {
  if (!condition) {
    throw new Error(message)
  }
}

/**
 * Generate a UUID
 */
export function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Get a promise that resolves after a given amount of time
 */
export function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Ensure that a passed value ends with a given string
 */
export function finalizeString(value: string, final: string) {
  return value.endsWith(final) ? value : value + final
}

import apiFetch from '@wordpress/api-fetch'

/**
 * Finalize a request
 */
export async function finalize(uuid: string, { nocharge = false }: { nocharge?: boolean } = {}) {
  let suffix = ''
  if (nocharge) {
    suffix = '?nocharge=1'
  }

  return await apiFetch<{
    publicPermalink: string
  }>({
    path: `/wp-json/ownbit-configurator/v1/finalize/${uuid}${suffix}`,
    method: 'POST',
  })
}

/**
 * Scroll to start of the page
 */
export function scrollTo(
  target: HTMLElement = document.querySelector('.ownbit-configurator') as HTMLElement,
) {
  window.scrollTo({
    top: target.offsetTop - 20,
    behavior: 'smooth',
  })
}

/**
 * Get a random integer
 */
export function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

/**
 * Check if a string is a valid URL
 */
export function isUrl(value: string): boolean {
  if (URL.canParse) return URL.canParse(value)

  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

/**
 * Removes Vue reactivity from a value - like Vue's built-in toRaw(), but recursive
 */
export function toRawDeep<T>(value: T): T {
  const rawValue = toRaw(value)
  if (typeof rawValue !== 'object' || rawValue === null) return rawValue

  if (Array.isArray(rawValue)) {
    return (rawValue as any).map(toRawDeep)
  } else {
    return Object.fromEntries(
      Object.entries(rawValue).map(([key, value]: any) => [key, toRawDeep(value)]),
    )
  }
}

import { isEqual as lodashIsEqual } from 'lodash-es'

/**
 * Check if two values are equal
 */
export function isEqual(a: any, b: any): boolean {
  return lodashIsEqual(toRawDeep(a), toRawDeep(b))
}

/**
 * Get an ID attribute for a field
 */
export function getFieldId(key: string, suffix?: number | string) {
  return suffix !== undefined ? `field-${key}-${suffix}` : `field-${key}`
}

// Length of the UUID + underscore prefix prepended to saved files
const UUID_PREFIX_LENGTH = 37

/**
 * Format a filename for display by removing the UUID prefix
 */
export function formatFilename(file: string | File) {
  if (typeof file !== 'string') return file.name
  const basename = file.slice(file.lastIndexOf('/') + 1)
  if (basename.length <= UUID_PREFIX_LENGTH) return file
  return file.slice(UUID_PREFIX_LENGTH)
}

/**
 * Escape a string for use in a regular expression
 */
export function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Check if a given MIME type is accepted
 */
export function checkMime(accepted: string | string[], mime: string): boolean {
  if (accepted.length === 0) return true

  const acceptedArray = Array.isArray(accepted) ? accepted : [accepted]

  for (const acceptedMime of acceptedArray) {
    if (acceptedMime.includes('*')) {
      const patternSource = acceptedMime
        .split('*')
        .map(part => escapeRegex(part))
        .join('.*')
      const pattern = new RegExp(`^${patternSource}$`, 'i')

      if (pattern.test(mime)) return true
    } else {
      if (acceptedMime === mime) return true
    }
  }

  return false
}

/**
 * Resolve a promise when a condition matches
 *
 * @param condition The callback to run for checking
 * @param interval How frequent to check the condition (interval in milliseconds)
 * @param timeout After how many milliseconds to reject the promise
 */
export async function poll(
  condition: () => boolean,
  interval: number,
  timeout?: number,
): Promise<void> {
  const startTime = Date.now()

  while (true) {
    if (typeof timeout === 'number' && Date.now() > startTime + timeout) {
      throw new Error('Condition not met before timeout')
    }

    const result = await condition()

    if (result) return

    await new Promise(resolve => setTimeout(resolve, interval))
  }
}

import QuickLRU from 'quick-lru'

/**
 * Convert HTML to plain text
 */
export function toPlainText(html: string): string {
  if (plainTextCache.has(html)) return plainTextCache.get(html)!

  div.innerHTML = html
  const result = div.textContent || ''
  plainTextCache.set(html, result)
  return result
}
const plainTextCache = new QuickLRU<string, string>({ maxSize: 50 })
const div = document.createElement('div')
